import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';

const ReadingPart6 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        setAnswers(migratedAnswers);
        setIsHydrated(true);
        return;
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved answers", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const {
    image_url,
    reading_text,
    story_text,
  } = exerciseData.content_data;

  const { questions } = exerciseData;

  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const handleAnswerChange = (questionId, event) => {
    setAnswers(prev => ({ ...prev, [questionId]: event.target.value }));
  };

  const renderFlowingText = () => {
    if (!reading_text) return null;

    const questionMap = new Map(questions.map(q => [q.order_index, q]));
    const parts = reading_text.split(/(\n|{{\d+}})/g).filter(Boolean);

    return parts.map((part, index) => {
      const gapMatch = part.match(/{{(\d+)}}/);

      if (gapMatch) {
        const qNumber = parseInt(gapMatch[1], 10);
        const question = questionMap.get(qNumber);
        if (!question) return '';

        if (question.order_index === 0) {
          return <strong key={question.id} className="exercise-example-text px-1">{question.accepted_answers[0]}</strong>;
        }
        return (
          <span key={question.id} className="inline-flex items-center gap-1">
            <strong className="exercise-gap-number">({qNumber})</strong>
            {(() => {
              // Fix: is_correct is at top level
              const isAnswerCorrect = question.is_correct === true || question.is_correct === 'true';
              const showIncorrectAnswerInReview = isReviewMode && !isAnswerCorrect;

              return (
                <>
                  {showIncorrectAnswerInReview ? (
                    <span className="exercise-input-text inline-flex items-center bg-red-50 px-2 py-0.5 rounded-lg border border-red-100 mx-1 align-middle">
                      <span style={{ textDecoration: 'line-through' }} className="text-red-500 opacity-70 mr-2 font-bold">{question.user_answer?.answer || '(empty)'}</span>
                      <span className="text-green-600 font-bold">{question.accepted_answers[0]}</span>
                    </span>
                  ) : (
                    <input
                      type="text"
                      value={showHint ? question.accepted_answers[0] : answers[question.id] || ''}
                      onChange={(e) => handleAnswerChange(question.id, e)}
                      className={`exercise-input-field exercise-input-text !w-[100px] text-center align-middle text-sm px-1 py-1 ${showHint ? 'hint-input' : ''}`}
                      readOnly={showHint || isReviewMode}
                      disabled={isReviewMode || !isHydrated}
                    />
                  )}
                  {isReviewMode && (isAnswerCorrect
                    ? <span className="correct-checkmark scale-75">✓</span>
                    : <span className="incorrect-checkmark scale-75">✗</span>
                  )}
                </>
              )
            })()}
          </span>
        );
      } else if (part === '\n') {
        return <br key={`br-${index}`} />;
      } else {
        return <span key={`text-${index}`}>{part}</span>;
      }
    });
  };

  return (
    <div className="w-full h-full flex flex-col justify-center max-w-4xl mx-auto">
      <div className="exercise-card p-4 flex-1 min-h-0 overflow-y-auto custom-scrollbar">
        {image_url && <img src={`${ASSET_BASE_URL}${image_url}`} alt="Story illustration" className="exercise-story-image mx-auto max-w-xl max-h-40 object-contain mb-4" />}

        <div className="exercise-story-box mt-0 mb-4 text-center">
          {story_text && <h3 className="exercise-story-title text-lg">{story_text}</h3>}
          <div className="exercise-body-text space-y-3 leading-snug text-sm text-left">
            {renderFlowingText()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadingPart6;